import { Lock, Save } from "lucide-react";
import { ActionButtonSpinner } from "./reimbursementActionButtonLoading";
import { cn } from "@/lib/utils/helpers";
import {
  getReimbursementSectionSubmitButtonLabel,
  type ReimbursementMilestoneStatus,
} from "../../utils/reimbursementMilestoneStatus";
import {
  isSectionSaveDisabled,
  isSectionSubmitDisabled,
  isSectionUnlockDisabled,
  shouldShowLibertyUnlockAction,
  shouldShowPmcSectionSaveSubmitActions,
  type ReimbursementSectionStatusValue,
} from "../../utils/reimbursementSectionStatus";

interface MilestoneSectionActionsProps {
  milestoneId: number;
  status: ReimbursementMilestoneStatus;
  sectionWorkflowStatus?: ReimbursementSectionStatusValue;
  /** When true, required fields/docs for this section are filled (uploaded, not underwriter-approved). */
  sectionSubmitReady?: boolean;
  /** Global locks for Submit (excludes form validity / required-field gates). */
  submitDisabled?: boolean;
  /** Global locks for Save as Draft (excludes form validity / required-field gates). */
  draftSaveDisabled?: boolean;
  saveLoading?: boolean;
  submitLoading?: boolean;
  onSaveDraft?: () => void;
  onSubmitClick?: () => void;
}

interface MilestoneUnlockActionProps {
  milestoneId: number;
  unlockDisabled?: boolean;
  unlockLoading?: boolean;
  onUnlock?: () => void;
}

export function MilestoneUnlockAction({
  milestoneId,
  unlockDisabled,
  unlockLoading,
  onUnlock,
}: MilestoneUnlockActionProps) {
  const disabled = Boolean(unlockDisabled) || Boolean(unlockLoading);

  return (
    <div className="mb-2 mt-2 flex w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={onUnlock}
        className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-[rgb(21_151_255)] bg-white px-3 text-sm font-semibold text-[rgb(21_151_255)] transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid={`reimbursement-status-bar-unlock-${milestoneId}`}
        aria-busy={unlockLoading}
      >
        {unlockLoading ? (
          <ActionButtonSpinner size={14} />
        ) : (
          <Lock size={14} aria-hidden="true" />
        )}
        {unlockLoading ? "Unlocking…" : "Unlock"}
      </button>
    </div>
  );
}

export function MilestoneSectionActions({
  milestoneId,
  sectionWorkflowStatus = null,
  sectionSubmitReady = false,
  submitDisabled: submitDisabledProp,
  draftSaveDisabled: draftSaveDisabledProp,
  saveLoading,
  submitLoading,
  onSaveDraft,
  onSubmitClick,
}: MilestoneSectionActionsProps) {
  if (!shouldShowPmcSectionSaveSubmitActions(sectionWorkflowStatus)) {
    return null;
  }

  const draftSaveDisabled =
    Boolean(draftSaveDisabledProp) ||
    Boolean(saveLoading) ||
    Boolean(submitLoading) ||
    isSectionSaveDisabled(sectionWorkflowStatus);
  const submitBlockedByWorkflow = isSectionSubmitDisabled(
    sectionWorkflowStatus,
    sectionSubmitReady,
  );
  const submitLockedByPeerAction =
    Boolean(submitDisabledProp) || Boolean(saveLoading);
  const submitDisabled =
    submitLockedByPeerAction ||
    Boolean(submitLoading) ||
    submitBlockedByWorkflow;

  const submitButtonVisualClass = (() => {
    if (submitLoading) {
      return "bg-gradient-to-b from-[rgb(44_160_255)] to-[rgb(12_128_220)] text-white";
    }
    if (!submitDisabled) {
      return "bg-gradient-to-b from-[rgb(44_160_255)] to-[rgb(12_128_220)] text-white hover:from-[#3b9ff7] hover:to-[#0b75c4]";
    }
    if (submitLockedByPeerAction && !submitBlockedByWorkflow) {
      return "bg-[#1597ff] text-white opacity-50";
    }
    return "bg-slate-100 text-slate-400";
  })();

  return (
    <div className="mb-2 mt-2 grid w-full grid-cols-2 gap-2">
      <button
        type="button"
        disabled={draftSaveDisabled}
        onClick={onSaveDraft}
        className="flex h-8 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid={`reimbursement-status-bar-save-draft-${milestoneId}`}
        aria-busy={saveLoading}
      >
        {saveLoading ? (
          <ActionButtonSpinner size={12} />
        ) : (
          <Save size={12} className="shrink-0" aria-hidden="true" />
        )}
        <span className="truncate">{saveLoading ? "Saving…" : "Save as Draft"}</span>
      </button>
      <button
        type="button"
        disabled={submitDisabled}
        onClick={onSubmitClick}
        className={cn(
          "flex h-8 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2 text-xs font-semibold transition disabled:cursor-not-allowed",
          submitButtonVisualClass,
        )}
        data-testid={`reimbursement-status-bar-submit-${milestoneId}`}
        aria-busy={submitLoading}
      >
        {submitLoading ? <ActionButtonSpinner size={12} className="text-white" /> : null}
        <span className="truncate">
          {getReimbursementSectionSubmitButtonLabel(milestoneId, {
            loading: submitLoading,
          })}
        </span>
      </button>
    </div>
  );
}

export interface ReimbursementMilestoneSectionActionRowProps {
  milestoneId: number;
  status: ReimbursementMilestoneStatus;
  sectionActionMode?: "pmc" | "liberty";
  showMilestoneSaveSubmit?: boolean;
  showDisabledUnlockForInProgress?: boolean;
  isLibertyRole?: boolean;
  isReimbursementConcluded?: boolean;
  sectionUnlocked?: Partial<Record<1 | 2 | 3, boolean>>;
  sectionWorkflowStatus?: ReimbursementSectionStatusValue;
  sectionSubmitReady?: boolean;
  submitDisabled?: boolean;
  draftSaveDisabled?: boolean;
  sectionActionsLocked?: boolean;
  sectionActionLoading?: {
    milestoneId: number;
    kind: "submit" | "unlock" | "draft";
  } | null;
  onSaveDraft?: () => void;
  onSubmitClick?: () => void;
  onUnlock?: () => void;
}

export function ReimbursementMilestoneSectionActionRow({
  milestoneId,
  status,
  sectionActionMode = "pmc",
  showMilestoneSaveSubmit = true,
  showDisabledUnlockForInProgress = false,
  isLibertyRole = false,
  isReimbursementConcluded = false,
  sectionUnlocked,
  sectionWorkflowStatus = null,
  sectionSubmitReady = false,
  submitDisabled,
  draftSaveDisabled,
  sectionActionsLocked,
  sectionActionLoading,
  onSaveDraft,
  onSubmitClick,
  onUnlock,
}: ReimbursementMilestoneSectionActionRowProps) {
  const unlockLoading =
    sectionActionLoading?.milestoneId === milestoneId &&
    sectionActionLoading.kind === "unlock";
  const submitLoading =
    sectionActionLoading?.milestoneId === milestoneId &&
    sectionActionLoading.kind === "submit";
  const draftSaveLoading =
    sectionActionLoading?.milestoneId === milestoneId &&
    sectionActionLoading.kind === "draft";
  const showUnlock =
    shouldShowLibertyUnlockAction(
      isLibertyRole,
      isReimbursementConcluded,
      sectionWorkflowStatus,
    ) ||
    (showDisabledUnlockForInProgress && isLibertyRole && !isReimbursementConcluded);
  const unlockDisabled =
    Boolean(sectionActionsLocked) ||
    isSectionUnlockDisabled(sectionWorkflowStatus, true) ||
    Boolean(sectionUnlocked?.[milestoneId as 1 | 2 | 3]);

  if (showUnlock) {
    return (
      <MilestoneUnlockAction
        milestoneId={milestoneId}
        unlockDisabled={unlockDisabled}
        unlockLoading={unlockLoading}
        onUnlock={onUnlock}
      />
    );
  }

  if (showMilestoneSaveSubmit && sectionActionMode === "pmc") {
    return (
      <MilestoneSectionActions
        milestoneId={milestoneId}
        status={status}
        sectionWorkflowStatus={sectionWorkflowStatus}
        sectionSubmitReady={sectionSubmitReady}
        submitDisabled={submitDisabled}
        draftSaveDisabled={draftSaveDisabled}
        saveLoading={draftSaveLoading}
        submitLoading={submitLoading}
        onSaveDraft={onSaveDraft}
        onSubmitClick={onSubmitClick}
      />
    );
  }

  return null;
}
